import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as React from 'react';
import { TranscriptionState, type Transcription } from '../../entities';
import { TrackingCategory, trackingService, TrackingTranscriptionParams } from '../../services';
import { AppConstantsContext } from '../AppConstants';

export interface TranscriptionStoreContextValue {
  id?: string | null;
  selectedTranscription: Transcription | null;
  transcriptions: Transcription[];
  setTranscriptions: (transcriptions: Transcription[]) => void;
  loading: boolean;
  showSelectedOnMobile: boolean;
  updateTranscriptions: () => void;
  updatePendingTranscriptions: () => void;
  setSelectedTranscription: (transcription: Transcription | null) => void;
  reset: () => void;
  setShowSelectedOnMobile: (show: boolean) => void;
  loadingCompleted?: boolean;
  error?: string;
}

export const TranscriptionStoreContext = createContext<TranscriptionStoreContextValue>({
  id: null,
  selectedTranscription: null,
  showSelectedOnMobile: false,
  transcriptions: [],
  setTranscriptions: () => {},
  loading: false,
  loadingCompleted: false,
  updateTranscriptions: () => {},
  updatePendingTranscriptions: () => {},
  setSelectedTranscription: () => {},
  reset: () => {},
  setShowSelectedOnMobile: () => {},
  error: null,
});

export const TranscriptionStoreContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [id, setId] = useState<string | null>(null);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [loading, setLoading] = useState(true);
  const { jobType } = useContext(AppConstantsContext);
  const [showSelectedOnMobile, setShowSelectedOnMobile] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setSelectedTranscription(null);
    setId(null);
  }, []);

  const updateTranscriptions = () => {
    setLoading(true);

    TranscriptionService.transcriptionGetMyFilesGet(jobType)
      .then((data) => {
        setTranscriptions(data);
        const transcription = data.find((t) => t.id === selectedTranscription?.id);
        setSelectedTranscription(transcription ?? data[0]);
      })
      .catch((err: any) => {
        console.error(err);
        setTranscriptions([]);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
        setLoadingCompleted(true);
      });
  };

  const setTranscription = useCallback((transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setId(transcription.id);
  }, []);

  const updatePendingTranscriptions = () => {
    transcriptions
      .filter(
        (transcription) =>
          transcription.state === TranscriptionState.Enqueued
          || transcription.state === TranscriptionState.Processing,
      )
      .forEach((transcription) => {
        TranscriptionService.transcriptionGetGet(transcription.id).then((updatedTranscr) => {
          setLoading(false);

          if (selectedTranscription?.id === updatedTranscr.id) {
            setSelectedTranscription((prev) => {
              if (prev?.id !== updatedTranscr.id) {
                return prev;
              }
              return {
                ...prev,
                progress: updatedTranscr.progress,
                state: updatedTranscr.state,
                error: updatedTranscr.error ?? prev.error,
              };
            });
          }
          // setState(null);
          if (updatedTranscr?.progress === 100) {
            try {
              const trackingValues = {
                location: TrackingTranscriptionParams.location.songbook,
                input:
                  transcription.source === 'youtube'
                    ? TrackingTranscriptionParams.input.youtube
                    : transcription.source,
                type: transcription.demo
                  ? TrackingTranscriptionParams.type.free
                  : TrackingTranscriptionParams.type.full,
                state: updatedTranscr.state,
                retranscribe: window.localStorage
                  .getItem('retranscribe')
                  ?.includes(transcription.id),
              };
              trackingService.valueObjectEvent(
                TrackingCategory.Transcription,
                TrackingTranscriptionParams.action.finish,
                { ...trackingValues },
              );
              window.localStorage.removeItem('retranscribe');
            } finally {
              setTranscription(updatedTranscr);
              if (updatedTranscr?.state === TranscriptionState.Ok) {
                // setState('success');
              } else if (
                updatedTranscr?.state === TranscriptionState.Error
                || updatedTranscr?.state === TranscriptionState.Refunded
              ) {
                setError(updatedTranscr?.error?.toString());
              }
            }
          }
          setTranscriptions((currentTranscriptions) => {
            const index = currentTranscriptions.findIndex((tr) => tr.id === updatedTranscr.id);
            if (index >= 0) {
              return [
                ...currentTranscriptions.slice(0, index),
                updatedTranscr,
                ...currentTranscriptions.slice(index + 1),
              ];
            }
            return [...currentTranscriptions, updatedTranscr];
          });
        });
      });
  };

  const contextValue: TranscriptionStoreContextValue = useMemo(
    () => ({
      id,
      selectedTranscription,
      setSelectedTranscription: setTranscription,
      reset,
      updateTranscriptions,
      transcriptions,
      setTranscriptions,
      updatePendingTranscriptions,
      loading,
      showSelectedOnMobile,
      setShowSelectedOnMobile,
      loadingCompleted,
      error,
    }),
    [
      id,
      selectedTranscription,
      reset,
      transcriptions,
      loading,
      setTranscription,
      error,
      showSelectedOnMobile,
      setShowSelectedOnMobile,
      loadingCompleted,
    ],
  );

  return (
    <TranscriptionStoreContext.Provider value={contextValue}>
      {children}
    </TranscriptionStoreContext.Provider>
  );
};
